// Pulse-specific range chip row: 6m · 1y · 2y · 3y · 5y · all.
//
// We don't reuse TimeRangeControl because Pulse owns a *different* range
// vocabulary than the rest of the dashboard (the others stick with the
// v1 1mo/12mo/24mo set). The shared range state (`?range=` URL param) is
// still respected, but Pulse normalizes legacy values to its own chips
// when reading.
import { useSearchParams } from 'react-router-dom';

export type PulseRangeKey = '6m' | '1y' | '2y' | '3y' | '5y' | 'all';

const CHIPS: { value: PulseRangeKey; label: string }[] = [
  { value: '6m', label: '6 mo' },
  { value: '1y', label: '1 yr' },
  { value: '2y', label: '2 yr' },
  { value: '3y', label: '3 yr' },
  { value: '5y', label: '5 yr' },
  { value: 'all', label: 'All' },
];

// v1 → v2 alias normalization, mirrors the FastAPI _LEGACY_RANGE_ALIASES.
const LEGACY: Record<string, PulseRangeKey> = {
  '1mo': '6m',
  '12mo': '1y',
  '24mo': '2y',
};

export function usePulseRange(defaultRange: PulseRangeKey = '2y'): PulseRangeKey {
  const [params] = useSearchParams();
  const raw = params.get('range') || defaultRange;
  if (CHIPS.some((c) => c.value === raw)) return raw as PulseRangeKey;
  if (LEGACY[raw]) return LEGACY[raw];
  return defaultRange;
}

export default function RangeChips({
  defaultRange = '2y',
}: { defaultRange?: PulseRangeKey } = {}) {
  const [params, setParams] = useSearchParams();
  const current = usePulseRange(defaultRange);

  const setRange = (value: PulseRangeKey) => {
    const next = new URLSearchParams(params);
    next.set('range', value);
    setParams(next, { replace: true });
  };

  return (
    <div className="pulse-range-chips" role="tablist" aria-label="Time range">
      {CHIPS.map((c) => (
        <button
          key={c.value}
          type="button"
          role="tab"
          aria-selected={current === c.value}
          className={`pulse-range-chip${current === c.value ? ' active' : ''}`}
          onClick={() => setRange(c.value)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
